import { extractApiError } from "@/lib/api";

/**
 * Map a checkout refusal to a sentence the buyer can read, in their own language.
 *
 * The backend answers `{"detail": {"code", "message"}}` on the money path
 * (modules/payments/service.py). The CODE is the contract — stable, machine-readable,
 * branchable; the `message` is a developer diagnostic written in English and must never
 * reach a French or Spanish buyer. So the code is what we translate, and
 * `extractApiError` is only the last resort, for refusals that predate the codes
 * (a 404 "Program not found", a 422 field list).
 *
 * Pure, and takes `t` as an argument rather than calling a hook: the buy handlers that
 * use it are callbacks, not components.
 *
 * @param {object} e         axios error
 * @param {function} t       translator bound to the "public" namespace
 * @param {string} fallback  page-level generic message
 * @returns {string}
 */
export function checkoutErrorMessage(e, t, fallback = "") {
  const detail = e?.response?.data?.detail;
  const code = detail && typeof detail === "object" && !Array.isArray(detail) ? detail.code : detail;
  const key = CHECKOUT_ERROR_KEYS[code];
  if (key) return t(key);
  return extractApiError(e, fallback) || fallback;
}

const CHECKOUT_ERROR_KEYS = {
  INVALID_PRODUCT: "checkout.error.invalidProduct",
  INVALID_PROGRAM: "checkout.error.invalidProgram",
  INVALID_PRICE: "checkout.error.invalidPrice",
  PAYMENT_PROVIDER_UNAVAILABLE: "checkout.error.providerUnavailable",
  // BUG-019. The buyer previewed a code that the server re-decided at payment (it can
  // expire, be deactivated or run out in between) — or typed one that never applied to
  // this kind of purchase. ONE key for all seven server-side refusals, matching the
  // backend, which deliberately does not say which one it is.
  PROMO_INVALID: "promo.invalid",
  // BUG-042. modules/payments/service.py:2202 raises 402 {"code": "GIFTCARD_DECLINED"}
  // when a stored-value card cannot cover (or no longer applies to) the purchase — e.g.
  // it was already redeemed, is cancelled, or its balance dropped between the buyer's
  // preview and the payment attempt. Before this key existed, that code had no entry
  // here, so it fell through to extractApiError's fallback: the buyer read whatever
  // English developer prose the backend attached, in every locale.
  GIFTCARD_DECLINED: "checkout.error.giftcardDeclined",
  // 2026-09-02. modules/gateway/flows/orders.py `quote_hosted_order` refuses a row priced
  // in a currency the platform does not settle (the settlement currency moved from CAD to
  // USD; rows priced before that kept "cad"). No exchange rate is invented, so the refusal
  // is right — but without this key its English developer diagnostic ("this platform
  // settles in USD, not CAD") reached the buyer raw. The storefront no longer offers such
  // a row for sale at all (`payable_online` on the public catalogue payload); this entry
  // covers the stale-page case where the click still happens.
  CURRENCY_NOT_SETTLEABLE: "checkout.error.currencyNotSettleable",
  // modules/payments/service.py answers 503 {"code": "PAYMENTS_UNAVAILABLE"} when no
  // usable Stripe key exists (demo mode) and no session can be opened. Same meaning as
  // the 502 PAYMENT_PROVIDER_UNAVAILABLE above, so the same sentence; without this entry
  // the buyer read "STRIPE_API_KEY is absent or the demo sentinel…" — an env-var name.
  PAYMENTS_UNAVAILABLE: "checkout.error.providerUnavailable",
  // modules/payments/service.py refuses to sell or enrol a course with no lessons (free or
  // paid). The storefront states it in advance (`unpayable_reason: "no_lessons"`); this
  // covers the stale page whose click still reaches the server.
  COURSE_HAS_NO_LESSONS: "learn.notAvailableYet",
};
